import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Box, Typography, LinearProgress, Stack } from '@mui/material';
import { keyframes } from '@mui/system';
// 다크/라이트 모드 공통 스피너 사용
import { Clock } from 'react-feather';
import { HexagonalNetworkSpinner } from './spinners';
import { useTheme } from '../contexts/ThemeContext';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeOut = keyframes`
  from { 
    opacity: 1;
  }
  to { 
    opacity: 0;
  }
`;

const slideUp = keyframes`
  from { 
    opacity: 0;
    transform: translateY(10px);
  }
  to { 
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
`;

const clockFloat = keyframes`
  0% { transform: translateY(0) rotate(-3deg); }
  50% { transform: translateY(-6px) rotate(-1deg); }
  100% { transform: translateY(0) rotate(-3deg); }
`;

/**
 * Loading Splash Screen
 * 앱 초기 로딩 또는 로그인 후 진입 시 표시되는 전체 화면 스플래시
 */
export const LoadingSplash = ({ 
  message = '시스템 초기화 중...',
  progress = null,
  onComplete = null,
  duration = 3000,
  customStages = null
}) => {
  const { colors, isDark } = useTheme();
  const [currentProgress, setCurrentProgress] = useState(5);
  const [currentMessage, setCurrentMessage] = useState(message);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const intervalStartedRef = useRef(false); // V30: duration 변경 시 인터벌 리셋 방지 (한 번만 시작)

  // 로딩 단계별 메시지 (최소 10%부터 시작하여 즉시 진행 중임을 표시)
  const loadingStages = useMemo(() => customStages || [
    { progress: 10, message: '시스템 연결 중...' },
    { progress: 25, message: '보안 인증 확인 중...' },
    { progress: 45, message: '데이터베이스 초기화 중...' },
    { progress: 65, message: 'AI 모델 로딩 중...' },
    { progress: 85, message: '대시보드 준비 중...' },
    { progress: 100, message: '완료!' }
  ], [customStages]);

  useEffect(() => {
    if (progress === null) {
      // V30: duration이 바뀔 때마다 인터벌이 리셋되어 스플래시가 끝나지 않던 문제 방지 - 최초 1회만 시작
      if (intervalStartedRef.current) return;
      intervalStartedRef.current = true;

      let currentStage = 0;
      const stageInterval = duration / loadingStages.length;

      setCurrentProgress(loadingStages[0].progress || 10);
      setCurrentMessage(loadingStages[0].message);
      currentStage = 1;

      const interval = setInterval(() => {
        if (currentStage < loadingStages.length) {
          setCurrentProgress(loadingStages[currentStage].progress);
          setCurrentMessage(loadingStages[currentStage].message);
          currentStage++;
        } else {
          clearInterval(interval);
          if (onComplete) {
            setIsFadingOut(true);
            setTimeout(() => {
              if (onComplete) onComplete();
            }, 400);
          }
        }
      }, stageInterval);

      return () => clearInterval(interval);
    } else {
      setCurrentProgress(Math.max(progress, 5));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progress, duration]);

  useEffect(() => {
    setCurrentMessage(message);
  }, [message]);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 20000, // 프리로더(z-index: 10000)보다 높게 설정하여 위에 표시
        backgroundColor: colors.primaryBg,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
        animation: isFadingOut 
          ? `${fadeOut} 0.4s ease-out forwards`
          : `${fadeIn} 0.6s ease-in`, // 프리로더와 부드럽게 전환되도록 시간 증가
        willChange: 'opacity',
        pointerEvents: isFadingOut ? 'none' : 'auto' // 페이드 아웃 중 클릭 방지
      }}
    >
      {/* 배경 그리드 패턴 - 즉시 표시 (지연 없음) */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.03, // 즉시 표시
          backgroundImage: `
            linear-gradient(${colors.primaryBorder} 1px, transparent 1px),
            linear-gradient(90deg, ${colors.primaryBorder} 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
          animation: `${pulse} 4s ease-in-out infinite`,
          willChange: 'opacity', // GPU 가속
          // 배경은 가장 먼저 렌더링
          zIndex: 0,
          contentVisibility: 'auto'
        }}
      />

      {/* Intelligence Hub 스타일 코너 장식 - 즉시 표시 (빠른 페이드인) */}
      <Box
        sx={{
          position: 'absolute',
          top: 20,
          left: 20,
          width: 80,
          height: 80,
          borderTop: `2px solid ${colors.accent}`,
          borderLeft: `2px solid ${colors.accent}`,
          opacity: 0.3,
          animation: `${fadeIn} 0.08s ease-in` // 매우 빠른 페이드인
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: 20,
          right: 20,
          width: 80,
          height: 80,
          borderTop: `2px solid ${colors.accent}`,
          borderRight: `2px solid ${colors.accent}`,
          opacity: 0.3,
          animation: `${fadeIn} 0.08s ease-in`
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: 20,
          left: 20,
          width: 80,
          height: 80,
          borderBottom: `2px solid ${colors.accent}`,
          borderLeft: `2px solid ${colors.accent}`,
          opacity: 0.3,
          animation: `${fadeIn} 0.08s ease-in`
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: 20,
          right: 20,
          width: 80,
          height: 80,
          borderBottom: `2px solid ${colors.accent}`,
          borderRight: `2px solid ${colors.accent}`,
          opacity: 0.3,
          animation: `${fadeIn} 0.08s ease-in`
        }}
      />

      {/* 메인 컨텐츠 */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 3,
          zIndex: 1,
          maxWidth: '600px',
          width: '100%',
          padding: '0 40px'
        }}
      >
        {/* 로고 및 타이틀 - 약간의 지연 후 빠르게 표시 */}
        <Box
          sx={{
            animation: `${slideUp} 0.12s ease-out 0.05s both`,
            textAlign: 'center',
            willChange: 'transform, opacity'
          }}
        >
          <Stack direction="row" justifyContent="center" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
            <Box
              sx={{
                width: 58,
                height: 58,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: isDark
                  ? 'radial-gradient(120% 120% at 30% 30%, rgba(255,255,255,0.18) 0%, rgba(123,104,238,0.35) 100%)'
                  : 'radial-gradient(120% 120% at 30% 30%, rgba(80,11,113,0.65) 0%, rgba(123,104,238,0.9) 100%)',
                boxShadow: isDark
                  ? '0 14px 30px rgba(0,0,0,0.55)'
                  : '0 12px 28px rgba(80,11,113,0.45)',
                border: isDark
                  ? '1px solid rgba(255,255,255,0.22)'
                  : '1px solid rgba(255,255,255,0.38)',
                animation: `${clockFloat} 2.6s ease-in-out infinite`
              }}
            >
              <Clock
                size={30}
                style={{
                  color: '#FFFFFF',
                  filter: 'drop-shadow(0 6px 14px rgba(0,0,0,0.45))'
                }}
              />
            </Box>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 900,
                fontSize: '2.5rem',
                color: '#FFFFFF',
                textShadow: isDark
                  ? '0 8px 24px rgba(0,0,0,0.55)'
                  : '0 8px 20px rgba(80,11,113,0.55)',
                letterSpacing: 0.4
              }}
            >
              OnTimeLog
            </Typography>
          </Stack>
        </Box>

        {/* 스피너 - 빠르게 표시 */}
        <Box
          sx={{
            animation: `${fadeIn} 0.15s ease-in 0.08s both`, // 0.08s 후 시작
            willChange: 'opacity' // GPU 가속
          }}
        >
          <HexagonalNetworkSpinner size={180} />
        </Box>

        {/* 로딩 메시지 및 Progress Bar - 빠르게 표시 */}
        <Box
          sx={{
            animation: `${fadeIn} 0.15s ease-in 0.1s both`, // 0.1s 후 시작
            textAlign: 'center',
            width: '100%',
            willChange: 'opacity' // GPU 가속
          }}
        >
          <Typography
            sx={{
              color: colors.primaryText,
              fontSize: '1rem',
              fontWeight: 500,
              marginBottom: 2
            }}
          >
            {currentMessage}
          </Typography>

          {/* 프로그레스 바 - 단일 (즉시 표시, 부드러운 진행) */}
          <Box sx={{ width: '100%', marginBottom: 1 }}>
            <LinearProgress
              variant="determinate"
              value={currentProgress}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: colors.secondaryBg,
                transition: 'width 0.6s ease-out', // 부드러운 진행 애니메이션
                '& .MuiLinearProgress-bar': {
                  backgroundColor: colors.accent,
                  borderRadius: 3,
                  transition: 'transform 0.6s ease-out' // 부드러운 진행 애니메이션
                }
              }}
            />
          </Box>

          <Typography
            sx={{
              color: colors.tertiaryText,
              fontSize: '0.75rem',
              fontWeight: 400
            }}
          >
            {currentProgress}% 완료
          </Typography>
        </Box>

        {/* 버전 정보 - 빠르게 표시 */}
        <Box
          sx={{
            animation: `${fadeIn} 0.2s ease-in 0.15s both`, // 더 빠르게
            position: 'absolute',
            bottom: 40,
            display: 'flex',
            gap: 3,
            color: colors.tertiaryText,
            fontSize: '0.75rem'
          }}
        >
          <span>V2.0</span>
          <span>•</span>
          <span>2025 Edition</span>
          <span>•</span>
          <span>Powered by THE CVI</span>
        </Box>
      </Box>

      {/* 스캔 라인 효과 */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: `linear-gradient(90deg, transparent, ${colors.accent}, transparent)`,
          animation: `${slideUp} 2s ease-in-out infinite`,
          '@keyframes slideDown': {
            '0%': { transform: 'translateY(0)' },
            '100%': { transform: 'translateY(100vh)' }
          }
        }}
      />
    </Box>
  );
};

export default LoadingSplash;
